// src/components/CardJubilee.js
import React from "react";
import PropTypes from "prop-types";
import { isZh } from "../utils/language"
import { toPinyin, toPinyinSync, capitalizePinyin } from "../utils/pinyinUtil";

/* =========================
   SUBJECT MAP
========================= */

const SUBJECT_BASE = {
  南海古佛: "Nan Hai Gu Fo",
  彌勒祖師: "Mi Le Zhu Shi",
  白水聖帝: "Bai Shui Sheng Di",
  金公祖師: "Kakek Guru Jin Gong",
  文慈菩薩: "Wen Ci Pusa",
  濟公活佛: "Ji Gong Huo Fo",
  關聖帝君: "Guan Sheng Di Jun",

  感謝諸天仙佛打幫助道: "Upacara Syukur Para Dewa Budha Membantu Pelaksanaan Tao",
  "過年除夕 辭歲與拜年": "Malam Tahun Baru Imlek - Mengantar Tahun Lama Menyambut Tahun Baru",

  中元節: "Festival Zhongyuan (Sembahyang Arwah)",
  中秋節: "Festival Zhongqiu (Pertengahan Musim Gugur)",
  端午節: "Festival Duanwu (Peh Cun)",
  清明節: "Festival Qingming (Ziarah Kubur)",
  冬至節: "Festival Dongzhi (Onde)",

  春季: "Musim Semi",
  夏季: "Musim Panas",
  秋季: "Musim Gugur",
  冬季: "Musim Dingin",
};

const SUBJECT_SUFFIX = {
  成道日: "Hari Mencapai Sempurna",
  聖誕: "Hari Kelahiran",
  老母大典: "Perayaan Agung Lao Mu",
  得道日: "Hari Pencerahan",
  農曆初一: "Imlek Tanggal 1 (Ce-It)",
  農曆十五: "Imlek Tanggal 15 (CapGo)",
};

const XIANGONG_MAP = {
  五盤: "5 Piring",
  廿五盤: "25 Piring",
};

const TATAKRAMA_MAP = {
  五叩首: "5x Koushou",
  初一十五: "Tata Krama Chuyi Shiwu",
  初一十五禮: "Tata Krama Chuyi Shiwu",
  三跪九叩: "San Gui Jiu Kou",
  三叩首: "3x Koushou",
  九五大禮初一十五禮: "Jiu Wu Da Li + Chuyi Shiwu",
  辭歲拜年: "Cisui Bainian",
};

export function translateSubject(text) {
  if (!text) return "";

  if (isZh()) return text;

  const s = text.trim();

  if (SUBJECT_BASE[s]) return SUBJECT_BASE[s];

  for (const key in SUBJECT_SUFFIX) {
    if (s.endsWith(key)) {
      const base = s.replace(key, "").trim();
      const baseText = SUBJECT_BASE[base] || capitalizePinyin(toPinyinSync(base));
      return `${SUBJECT_SUFFIX[key]} <i>${baseText}</i>`;
    }
  }

  return capitalizePinyin(toPinyinSync(s));
}

function translateXianGong(text) {
  if (!text) return "";

  if (isZh()) return text;

  if (XIANGONG_MAP[text]) return XIANGONG_MAP[text];

  return capitalizePinyin(toPinyinSync(text));
}

function translateTataKrama(text) {
  if (!text) return "";

  if (isZh()) return text;

  if (TATAKRAMA_MAP[text]) return TATAKRAMA_MAP[text];

  return capitalizePinyin(toPinyinSync(text));
}

function decodeText(raw) {
  if (!raw) return raw;
  let s = String(raw).trim();
  // basic decoding helpers
  const decodeHtmlEntities = (str) => {
    try {
      const txt = document.createElement("textarea");
      txt.innerHTML = str;
      return txt.value;
    } catch (e) {
      return str;
    }
  };
  const decodeNumericEntities = (str) => str.replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)));
  const decodeUnicodeEscapes = (str) =>
    str.replace(/\\\\u/g, "\\u").replace(/\\u([\dA-Fa-f]{4})/g, (_, g) => String.fromCharCode(parseInt(g, 16)));

  s = decodeHtmlEntities(s);
  s = decodeNumericEntities(s);
  s = decodeUnicodeEscapes(s);
  s = s.replace(/\u0000/g, "").replace(/\uFFFD/g, "");
  return s;
}

export default function CardJubilee({ jub }) {
  const xianGong = decodeText(jub.jubilee_meta?.xianGong ?? "");
  const tataKrama = decodeText(jub.jubilee_meta?.tataKrama ?? "");

  const subjectText = isZh() ? decodeText(jub.subject) : translateSubject(decodeText(jub.subject));
  const xianGongText = isZh() ? xianGong : translateXianGong(jub.jubilee_meta?.xianGong);
  const tataKramaText = isZh() ? tataKrama : translateTataKrama(jub.jubilee_meta?.tataKrama);

  return (
    <div
      className="jubilee-card"
      role="group"
      aria-label="Jubilee"
      style={{
        background: "linear-gradient(90deg,#fff4f0,#fffaf6)",
        border: "1px solid #ffd6c2",
        padding: "0.625rem",
        borderRadius: "0.75rem",
        marginBottom: "0.5rem",
        fontWeight: 700,
        boxShadow: "0 0.125rem 0.375rem rgba(255,160,110,0.12)",
        minWidth: "15.625rem",
      }}
    >
      <div style={{ fontSize: "1rem" }} dangerouslySetInnerHTML={{ __html: subjectText }} />
      <div style={{ fontSize: "0.875rem", color: "#666", marginTop: "0.25rem" }}>
        {xianGongText}{" "}
        <span style={{ paddingLeft: "0.3rem" }}>{tataKramaText}</span>
      </div>
    </div>
  );
}

CardJubilee.propTypes = {
  jub: PropTypes.object.isRequired,
};
